/**
 * Аналіз рахунку (ч.1038): сальдо на початок, обороти в розрізі
 * кореспондуючих рахунків, сальдо на кінець — «Анализ счета» 1С.
 * Клік по кореспондуючому рахунку — картка з відбором по ньому.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accounts, reports, type AccountAnalysisRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import LedgerFilters, { NoRegulatedAccess } from './LedgerFilters';
import { useLedgerParams } from './useLedgerParams';
import { numCell, periodLabel } from './ledgerColumns';
import { ledgerLinks, money } from './ledgerModel';

type Row = AccountAnalysisRow & { _kind?: 'total' };

export default function AccountAnalysisPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const account = st.get('account');
  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accounts.list({ only_postable: false, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const q = useQuery({
    queryKey: ['ledger-an', st.org, account, st.from, st.to],
    queryFn: () => reports.accountAnalysis({ organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to }),
    enabled: !!st.org && !!account,
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };
  const rows = useMemo<Row[]>(() => data
    ? [...data.rows, { corr_account_code: '', corr_account_name: 'Разом', turnover_dt: data.totals.turnover_dt, turnover_cr: data.totals.turnover_cr, _kind: 'total' }]
    : [], [data]);
  const cardLink = (r: Row) => ledgerLinks.accountCard({ ...period, account, corr: r.corr_account_code });

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    { key: 'corr', header: 'Кореспондуючий рахунок', width: 420,
      cell: (r) => r._kind ? <strong>{r.corr_account_name}</strong> : (
        <a className="ledger__link" title="Картка рахунку з відбором по цьому кореспондуючому рахунку"
          onClick={(e) => { e.stopPropagation(); navigate(cardLink(r)); }}>
          {r.corr_account_code} {r.corr_account_name}
        </a>) },
    { key: 'turnover_dt', header: <>З кредиту в дебет<br />(Дт {account})</>, width: 170, align: 'right', cell: (r) => numCell(r.turnover_dt) },
    { key: 'turnover_cr', header: <>З дебету в кредит<br />(Кт {account})</>, width: 170, align: 'right', cell: (r) => numCell(r.turnover_cr) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [account, st.org, st.from, st.to]);

  if (!st.permsLoading && !st.hasPermission) return <NoRegulatedAccess title="Аналіз рахунку" total={st.totalAccessibleOrgs} />;
  const accList = (accountsQ.data ?? []).slice().sort((a, b) => a.code.localeCompare(b.code));
  const t = data?.totals;
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{data ? `Аналіз рахунку ${data.account_code} ${data.account_name}` : `Аналіз рахунку ${account}`}
          {' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
        {account && (
          <span className="ledger__tree-actions">
            <Link className="btn-link" to={ledgerLinks.trialBalanceAccount({ ...period, account })}>ОСВ по рахунку</Link>
            <Link className="btn-link" to={ledgerLinks.accountTurnover({ ...period, account })}>обороти</Link>
            <Link className="btn-link" to={ledgerLinks.accountCard({ ...period, account })}>картка</Link>
          </span>
        )}
      </div>
      <LedgerFilters state={st}
        printHref={account ? ledgerLinks.print('an', { ...period, account }) : undefined}
        onExcel={account ? () => reports.ledgerXlsx('/reports/account-analysis', { organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to }) : undefined}
        disabled={!data}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Рахунок</label>
          <select value={account} onChange={(e) => st.set({ account: e.target.value })}>
            <option value="">— оберіть —</option>
            {accList.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
      </LedgerFilters>
      {t && (
        <div className="ledger__totals" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          <span>Сальдо на початок: Дт <strong>{money(t.opening_dt, { zero: '0,00' })}</strong> · Кт <strong>{money(t.opening_cr, { zero: '0,00' })}</strong></span>
          <span>Сальдо на кінець: Дт <strong>{money(t.closing_dt, { zero: '0,00' })}</strong> · Кт <strong>{money(t.closing_cr, { zero: '0,00' })}</strong></span>
        </div>
      )}
      {q.isError && <p className="ledger__warn">Не вдалося завантажити звіт: перевірте код рахунку.</p>}
      <DataTable<Row>
        rows={rows} columns={cols}
        rowKey={(r) => r._kind ? '_total' : r.corr_account_code}
        loading={q.isLoading}
        emptyText={account ? 'За період оборотів по рахунку немає.' : 'Оберіть рахунок.'}
        rowBold={(r) => !!r._kind}
        rowBackground={(r) => r._kind ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => { if (!r._kind) navigate(cardLink(r)); }}
        storageKey="ledger-account-analysis"
      />
      {data && <div className="ledger__totals"><span className="ledger__hint">клік по кореспондуючому рахунку — картка з відбором по ньому</span></div>}
    </div>
  );
}
